import { useCallback, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { ConfirmationDialog } from '@terra/ui-design-system';
import { FindingStatusSchema } from '@terra/shared-schemas-orchestrator';

export function isPublishedAfterFindingSave({
  originalIsPublished,
  isPublishedUpdate,
}: {
  originalIsPublished: boolean | null | undefined;
  isPublishedUpdate: boolean | null | undefined;
}): boolean | null | undefined {
  return isPublishedUpdate === undefined
    ? originalIsPublished
    : isPublishedUpdate;
}

export function requiresUnpublishedFindingSignalConfirm({
  isPublished,
  nextStatus,
  signalId,
}: {
  isPublished: boolean | null | undefined;
  nextStatus: string;
  signalId?: string | null;
}): boolean {
  return (
    !isPublished &&
    nextStatus === FindingStatusSchema.enum.FalsePositive &&
    Boolean(signalId)
  );
}

export function useUnpublishedFindingSignalConfirm() {
  const t = useTranslations('findingsTab.row');
  const tCommon = useTranslations('common');
  const resolverRef = useRef<((confirmed: boolean) => void) | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const settle = useCallback((confirmed: boolean) => {
    const resolve = resolverRef.current;

    resolverRef.current = null;
    setIsOpen(false);
    resolve?.(confirmed);
  }, []);

  const confirmDisproveSignal = useCallback(
    () =>
      new Promise<boolean>((resolve) => {
        resolverRef.current = resolve;
        setIsOpen(true);
      }),
    [],
  );

  const dialog = (
    <ConfirmationDialog
      isOpen={isOpen}
      onOpenChange={({ open }) => {
        if (!open) {
          settle(false);
        }
      }}
      onConfirm={() => settle(true)}
      onCancel={() => settle(false)}
      title={t('disproveSignalTitle')}
      description={t('disproveSignalDescription')}
      confirmText={tCommon('confirm')}
      cancelText={tCommon('cancel')}
    />
  );

  return { confirmDisproveSignal, dialog };
}
